import { Group, Line, Shape, Text } from "react-konva";
import type { KonvaEventObject } from "konva/lib/Node";
import { memo, useMemo, useState } from "react";
import type { Furniture } from "../../lib/types";
import { resolveFurniture } from "../../lib/furniture/resolve";
import { buildRenderPlan, lodForPx } from "../../lib/furniture/renderPlan";
import { tracePath } from "../../lib/furniture/path";
import { PX_PER_IN } from "../../lib/furniture/materials";
import { labelColorFor } from "../../lib/furniture/color";
import { boundingBox } from "../../lib/geom";
import { annotationScale, labelFits, uprightFlip } from "../../lib/canvasLabels";

/** Name label size and inset at zoom 1, px. */
const LABEL_FONT_PX = 11;
const LABEL_PAD_PX = 5;

/** Selection / attention colors shared with the rest of the canvas. */
export const FURNITURE_SELECTED_STROKE = "#2a70f5";
export const FURNITURE_FLASH_STROKE = "#a855f7";
export const FURNITURE_UNNAMED_STROKE = "#f43f5e";

export interface FurnitureNodeProps {
  furniture: Furniture;
  /** Pixels per inch at the current fit (pan/zoom is applied by the Stage). */
  pxPerIn: number;
  /** Center position in canvas pixels. */
  center: { x: number; y: number };
  isSelected: boolean;
  isFlashed: boolean;
  /** Overall stage zoom (used only to choose the LOD). */
  stageScale?: number;
  draggable?: boolean;
  /** Pointer travel before a drag starts, px (Konva's default when unset). */
  dragDistance?: number;
  /** Picked up by a press-and-hold: raised shadow and a slight scale-up. */
  lifted?: boolean;
  /** Faded back while another piece is in Adjust mode. */
  dimmed?: boolean;
  onMouseDown?: (e: KonvaEventObject<MouseEvent>) => void;
  onTouchStart?: (e: KonvaEventObject<TouchEvent>) => void;
  onTap?: (e: KonvaEventObject<Event>) => void;
  onContextMenu?: (e: KonvaEventObject<PointerEvent>) => void;
  onDragStart?: (e: KonvaEventObject<DragEvent>) => void;
  onDragMove?: (e: KonvaEventObject<DragEvent>) => void;
  onDragEnd?: (e: KonvaEventObject<DragEvent>) => void;
  onDblClick?: (e: KonvaEventObject<MouseEvent>) => void;
  onDblTap?: (e: KonvaEventObject<Event>) => void;
  onMouseMove?: (e: KonvaEventObject<MouseEvent>) => void;
  onMouseLeave?: (e: KonvaEventObject<MouseEvent>) => void;
  /** Leave the stage cursor alone (a sink drag owns it). */
  holdCursor?: boolean;
  /** Render the label. Defaults to true. */
  showLabel?: boolean;
  /** Extra children rendered inside the rotated group, in canvas px. */
  children?: React.ReactNode;
}

/**
 * Draws one piece of furniture: a footprint silhouette with a drop shadow, the
 * resolved parts (seats, arms, cushions, tops…) painted bottom-to-top with
 * their material tiles, decorative seams, a zoom-stable selection outline,
 * and the name label.
 *
 * Geometry is drawn in *inch space* inside a group scaled by `pxPerIn`, so
 * the (memoized) path commands never change when the canvas resizes or
 * zooms — only the group scale does. Strokes opt out of scaling so line
 * weights stay constant on screen.
 */
export const FurnitureNode = memo(function FurnitureNode({
  furniture: f,
  pxPerIn,
  center,
  isSelected,
  isFlashed,
  stageScale = 1,
  draggable = false,
  dragDistance,
  lifted = false,
  dimmed = false,
  onMouseDown,
  onTouchStart,
  onTap,
  onContextMenu,
  onDragStart,
  onDragMove,
  onDragEnd,
  onDblClick,
  onDblTap,
  onMouseMove,
  onMouseLeave,
  holdCursor = false,
  showLabel = true,
  children,
}: FurnitureNodeProps) {
  const resolved = resolveFurniture(f);
  const wPx = f.width * pxPerIn;
  const dPx = f.depth * pxPerIn;
  const minScreenPx = Math.min(wPx, dPx) * stageScale;
  const lod = lodForPx(minScreenPx);
  const plan = useMemo(() => buildRenderPlan(resolved, lod), [resolved, lod]);
  const isUnnamed = !f.name.trim();
  const [hovered, setHovered] = useState(false);

  const outlineStroke = isFlashed
    ? FURNITURE_FLASH_STROKE
    : isSelected
      ? FURNITURE_SELECTED_STROKE
      : isUnnamed
        ? FURNITURE_UNNAMED_STROKE
        : hovered && draggable
          ? "rgba(42, 112, 245, 0.6)"
          : "rgba(20, 20, 28, 0.22)";
  const outlineWidth = isFlashed ? 3 : isSelected ? 2 : isUnnamed || (hovered && draggable) ? 1.5 : 1;

  // Shadows are painted in inch space (they scale with the piece). Cap the
  // blur so huge rugs don't get a giant halo and tiny stools still get lift.
  const dropBlur = clampNum(
    (isFlashed ? 24 : lifted ? 28 : isSelected ? 16 : 8) / Math.max(pxPerIn, 0.01),
    0.4,
    8
  );
  const dropOffsetY = clampNum(
    (lifted ? 9 : isSelected ? 5 : 2.5) / Math.max(pxPerIn, 0.01),
    0.15,
    3
  );
  const partShadows = lod >= 2 && minScreenPx > 90;

  const labelBox = useMemo(() => boundingBox(resolved.footprint), [resolved]);
  const labelFill = labelColorFor(plan.parts.length ? plan.parts[plan.parts.length - 1].paint.fill : plan.baseFill);
  const labelText = isUnnamed ? "Unnamed" : f.name;
  const labelK = annotationScale(stageScale);
  const labelPad = LABEL_PAD_PX * labelK;
  const flip = uprightFlip(f.rotation);
  const boxWPx = labelBox.width * pxPerIn;
  const boxHPx = labelBox.height * pxPerIn;
  const drawLabel =
    showLabel &&
    labelFits(
      labelText,
      LABEL_FONT_PX * labelK * stageScale,
      boxWPx * stageScale,
      boxHPx * stageScale,
      labelPad * stageScale
    );

  return (
    <Group
      name="furniture-node"
      id={f.id}
      x={center.x}
      y={center.y}
      rotation={f.rotation}
      scaleX={lifted ? 1.03 : 1}
      scaleY={lifted ? 1.03 : 1}
      draggable={draggable}
      dragDistance={dragDistance}
      opacity={(isUnnamed ? 0.8 : 1) * (dimmed ? 0.4 : 1)}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      onTap={onTap}
      onContextMenu={onContextMenu}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      onDblClick={onDblClick}
      onDblTap={onDblTap}
      onMouseMove={onMouseMove}
      onMouseEnter={(e) => {
        setHovered(true);
        if (holdCursor) return;
        const stage = e.target.getStage();
        if (stage && draggable) stage.container().style.cursor = "grab";
      }}
      onMouseLeave={(e) => {
        setHovered(false);
        onMouseLeave?.(e);
        if (holdCursor) return;
        const stage = e.target.getStage();
        if (stage) stage.container().style.cursor = "default";
      }}
    >
      <Group scaleX={pxPerIn} scaleY={pxPerIn}>
        {/* Footprint base: hit region + drop shadow + silhouette fill. */}
        <Shape
          sceneFunc={(ctx, shape) => {
            tracePath(ctx, plan.footprintCmds);
            ctx.fillStrokeShape(shape);
          }}
          fill={plan.baseFill}
          shadowColor={isFlashed ? FURNITURE_FLASH_STROKE : "#0b0d14"}
          shadowBlur={dropBlur}
          shadowOpacity={isFlashed ? 0.6 : lifted ? 0.3 : isSelected ? 0.22 : 0.16}
          shadowOffsetY={dropOffsetY}
          shadowForStrokeEnabled={false}
          perfectDrawEnabled={false}
        />
        {/* Parts, bottom to top. Non-listening so the footprint owns hits. */}
        {plan.parts.map((pd) => {
          const patternScale = 1 / PX_PER_IN;
          const usePattern = pd.tile !== null;
          const shadowIn = partShadows ? pd.paint.castShadowIn : 0;
          return (
            <Shape
              key={pd.part.id}
              listening={false}
              sceneFunc={(ctx, shape) => {
                tracePath(ctx, pd.cmds);
                ctx.fillStrokeShape(shape);
              }}
              fill={pd.paint.fill}
              fillPriority={usePattern ? "pattern" : "color"}
              fillPatternImage={usePattern ? (pd.tile!.canvas as unknown as HTMLImageElement) : undefined}
              fillPatternRepeat="repeat"
              fillPatternScaleX={patternScale}
              fillPatternScaleY={patternScale}
              fillPatternRotation={pd.patternRotation}
              stroke={pd.paint.stroke}
              strokeWidth={0.9}
              strokeScaleEnabled={false}
              opacity={pd.paint.opacity}
              shadowEnabled={shadowIn > 0}
              shadowColor="#0b0d14"
              shadowBlur={shadowIn}
              shadowOpacity={0.22}
              shadowOffsetX={shadowIn * 0.25}
              shadowOffsetY={shadowIn * 0.45}
              shadowForStrokeEnabled={false}
              perfectDrawEnabled={false}
            />
          );
        })}
        {/* Seams. */}
        {plan.parts.map((pd) =>
          pd.seams.map((s, i) => (
            <Line
              key={`${pd.part.id}-seam-${i}`}
              listening={false}
              points={[s.a.x, s.a.y, s.b.x, s.b.y]}
              stroke={pd.paint.seam}
              strokeWidth={0.9}
              strokeScaleEnabled={false}
              lineCap="round"
              perfectDrawEnabled={false}
            />
          ))
        )}
        {/* Selection / draft outline on top so parts can't cover it. */}
        <Shape
          listening={false}
          sceneFunc={(ctx, shape) => {
            tracePath(ctx, plan.footprintCmds);
            ctx.fillStrokeShape(shape);
          }}
          stroke={outlineStroke}
          strokeWidth={outlineWidth}
          strokeScaleEnabled={false}
          dash={isUnnamed && !isSelected ? [6, 4] : undefined}
          dashEnabled={isUnnamed && !isSelected}
          perfectDrawEnabled={false}
        />
      </Group>
      {drawLabel && (
        // Pinned to the footprint's top-left corner as read on screen: a
        // flipped piece anchors at the opposite corner of its own frame.
        <Group rotation={flip} listening={false}>
          <Text
            x={(flip ? -labelBox.maxX : labelBox.minX) * pxPerIn + labelPad}
            y={(flip ? -labelBox.maxY : labelBox.minY) * pxPerIn + labelPad}
            scaleX={labelK}
            scaleY={labelK}
            width={Math.max(10, (boxWPx - labelPad * 2) / labelK)}
            text={labelText}
            fontSize={LABEL_FONT_PX}
            fontFamily="Inter, sans-serif"
            fontStyle={isUnnamed ? "italic 600" : "600"}
            fill={labelFill}
            ellipsis
            wrap="none"
            shadowColor="rgba(0,0,0,0.35)"
            shadowBlur={2}
            shadowEnabled={labelFill.startsWith("rgba(255")}
          />
        </Group>
      )}
      {children}
    </Group>
  );
});

function clampNum(v: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, v));
}
